<template>
  <div class="article-page">
    <div v-if="pending" class="loading-state">
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
        <path fill="#5B6FB5" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
          <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur=".8s" repeatCount="indefinite" />
        </path>
      </svg>
    </div>

    <div v-else-if="error || !data" class="error-state">
      <h1>文章不存在</h1>
      <p>无法找到该文章，可能已被删除或地址错误。</p>
      <NuxtLink to="/blog" class="back-link">返回文章列表</NuxtLink>
    </div>

    <div v-else-if="data.requiresAccess" class="private-access-state">
      <div class="private-card">
        <div class="lock-icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
            <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3a3 3 0 0 0-3 3v6.75a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3v-6.75a3 3 0 0 0-3-3v-3c0-2.9-2.35-5.25-5.25-5.25Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd" />
          </svg>
        </div>
        <h1>{{ data.article?.Title || '私密文章' }}</h1>
        <p class="hint-text">此文章需要访问码才能查看</p>

        <!-- 验证访问码 -->
        <div class="access-form">
          <input 
            v-model="accessCode" 
            type="text" 
            placeholder="请输入访问码" 
            maxlength="8"
            :disabled="verifying"
            @keyup.enter="verifyCode"
          />
          <button 
            :disabled="!accessCode || verifying" 
            @click="verifyCode"
          >
            {{ verifying ? '验证中...' : '查看内容' }}
          </button>
        </div>
        <p v-if="verifyError" class="error-text">{{ verifyError }}</p>

        <!-- 申请访问码 -->
        <div class="divider">
          <span>或</span>
        </div>
        
        <div v-if="!requestSent" class="request-section">
          <p class="request-hint">没有访问码？可以向博主申请</p>
          <textarea 
            v-model="requestNote" 
            placeholder="简单说明一下申请理由（选填）" 
            rows="2"
            :disabled="requesting"
          ></textarea>
          <button 
            class="request-btn"
            :disabled="requesting"
            @click="requestAccess"
          >
            {{ requesting ? '提交中...' : '申请访问码' }}
          </button>
        </div>

        <div v-else class="request-result">
          <p class="success-text">✓ 申请已提交</p>
          <p class="code-display">您的访问码：<strong>{{ generatedCode }}</strong></p>
          <p class="info-text">请等待管理员审批后，使用此访问码查看内容</p>
        </div>

        <NuxtLink to="/blog" class="back-link">← 返回文章列表</NuxtLink>
      </div>
    </div>

    <div v-else class="blog-detail-container">
      <!-- 顶部 Header/Hero 区域 -->
      <div class="standard-header">
        <div class="cover-wrapper" v-if="data.article.CoverImage">
          <ClientOnly>
              <BlurhashImage
                :src="getImageUrl(data.article.CoverImage)"
                :hash="data.article.CoverBlurHash || 'L6PZfSi_.AyE_3t7t7R**0o#DgR4'"
                :alt="data.article.Title"
                class="article-cover"
              />
          </ClientOnly>
        </div>
        <div class="title-block">
          <h1>{{ data.article.Title }}</h1>
          <div class="meta">
            <span>{{ formatDate(data.article.CreatedAt) }}</span>
            <span v-if="data.article.TypeName">{{ data.article.TypeName }}</span>
            <span class="reading-time" v-if="readingTime > 0">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              约 {{ readingTime }} 分钟阅读
            </span>
          </div>
        </div>
      </div>

      <div class="article-layout">
        <!-- 左侧 TOC 侧边栏（大屏显示） -->
        <div class="toc-sidebar-wrapper">
          <UnifiedSidebar
            v-if="tocItems.length > 0"
            mode="toc"
            title="目录"
            :items="tocItems"
            :active-id="activeTocId"
            :is-open="isTocOpen"
            @select="handleTocSelect"
            @close="closeTocSidebar"
          />
        </div>

        <!-- 文章主体内容 -->
        <div class="article-content-wrapper">
          <!-- 使用 ArticleContent 组件渲染（支持地图动态加载） -->
          <ArticleContent :content="data.article.Content" />

          <!-- 文章导航（上下篇 + 相关推荐），私密文章不显示 -->
          <ArticleNavigation
            v-if="showNavigation"
            :prev="navigation?.prev"
            :next="navigation?.next"
            :related="navigation?.related"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTableOfContents } from '@/composables/useTableOfContents'
import type { SidebarItem } from '~/components/UnifiedSidebar.vue'

const route = useRoute()
const config = useRuntimeConfig()
const slug = route.params.slug as string

interface ArticleDetail {
  ArticleId: number
  Title: string
  Slug: string
  Summary: string | null
  Content: string
  CoverImage: string | null
  CoverBlurHash: string | null
  TypeId: number
  TypeName?: string
  TypeSlug?: string
  Renderer?: string
  SeriesId: number | null
  SeriesName?: string
  IsPublic: number
  Status: number
  CreatedAt: string
  UpdatedAt: string
}

interface ApiResponse {
  article: ArticleDetail
  requiresAccess?: boolean
  message?: string
}

/**
 * 【性能优化】公开文章缓存
 * 使用 useState 做全局缓存，缓存 1 小时
 * 私密文章不缓存，每次请求验证 token
 */
const CACHE_DURATION = 60 * 60 * 1000 // 1 小时

interface CachedArticle {
  data: ApiResponse
  timestamp: number
}

// 全局文章缓存（按 slug 存储）
const articlesCacheMap = useState<Record<string, CachedArticle>>('blog-articles-detail-cache', () => ({}))

// 检查缓存是否有效
const isCacheValid = (cache: CachedArticle | undefined): cache is CachedArticle => {
  if (!cache) return false
  return Date.now() - cache.timestamp < CACHE_DURATION
}

// 获取存储的 accessCode（客户端）
const getSavedAccessCode = (articleSlug: string): string | null => {
  if (import.meta.server) return null
  // 尝试通过 slug 对应的 key 获取，或者通用 key
  return localStorage.getItem(`access_code_slug_${articleSlug}`) || null
}

// ========== 文章导航数据（上下篇 + 相关推荐） ==========
interface NavigationData {
  prev: {
    ArticleId: number
    Title: string
    Slug: string
    Summary?: string | null
    CoverImage?: string | null
    CoverBlurHash?: string | null
    TypeName?: string
  } | null
  next: {
    ArticleId: number
    Title: string
    Slug: string
    Summary?: string | null
    CoverImage?: string | null
    CoverBlurHash?: string | null
    TypeName?: string
  } | null
  related: Array<{
    ArticleId: number
    Title: string
    Slug: string
    Summary?: string | null
    CoverImage?: string | null
    CoverBlurHash?: string | null
    TypeName?: string
  }>
}

// 并行获取文章和导航数据
const [articleResult, navigationResult] = await Promise.all([
  // 文章数据
  useAsyncData(`article-${slug}`, async () => {
    const savedCode = getSavedAccessCode(slug)
    
    const cached = articlesCacheMap.value[slug]
    if (isCacheValid(cached) && !cached.data.requiresAccess && cached.data.article?.IsPublic === 1) {
      return cached.data
    }
    
    const result = await $fetch<ApiResponse>(`${config.public.apiBase}/articles/${slug}`, {
      query: savedCode ? { accessCode: savedCode } : undefined
    })
    
    if (result && !result.requiresAccess && result.article?.IsPublic === 1) {
      articlesCacheMap.value[slug] = {
        data: result,
        timestamp: Date.now()
      }
    }
    
    return result
  }, { server: true }),
  
  // 导航数据（并行获取，不依赖文章结果）
  useAsyncData(`article-nav-${slug}`, async (): Promise<NavigationData | null> => {
    try {
      return await $fetch<NavigationData>(`${config.public.apiBase}/articles/${slug}/navigation`)
    } catch (e: any) {
      // 忽略 404 错误（文章不存在或无权限时是正常的）
      if (e?.statusCode !== 404 && e?.response?.status !== 404) {
        console.warn('Failed to fetch article navigation:', e.message || String(e))
      }
      return null
    }
  }, { server: true })
])

const { data, status, error, refresh } = articleResult
const { data: navigation } = navigationResult

const pending = computed(() => status.value === 'pending')

// 私密文章时隐藏导航（在模板中使用）
const showNavigation = computed(() => {
  return navigation.value && !data.value?.requiresAccess
})

const isTravelMode = computed(() => {
  if (!data.value?.article) return false
  // 判断是否使用 TravelMarkdown 渲染器
  return data.value.article.Renderer === 'TravelMarkdown'
})

// ========== 阅读时间计算 ==========
const readingTime = computed(() => {
  if (!data.value?.article?.Content) return 0
  const content = data.value.article.Content
  // 移除代码块和 HTML 标签
  const textContent = content
    .replace(/```[\s\S]*?```/g, '') // 移除代码块
    .replace(/<[^>]+>/g, '') // 移除 HTML 标签
    .replace(/[#*`~\[\]()]/g, '') // 移除 Markdown 符号
  
  // 计算中文字符数（中文阅读速度约 300-500 字/分钟，取 400）
  const chineseChars = (textContent.match(/[\u4e00-\u9fa5]/g) || []).length
  // 计算英文单词数（英文阅读速度约 200 词/分钟）
  const englishWords = (textContent.match(/[a-zA-Z]+/g) || []).length
  
  // 综合计算阅读时间（分钟）
  const minutes = Math.ceil(chineseChars / 400 + englishWords / 200)
  return Math.max(1, minutes) // 至少 1 分钟
})

// ========== 私密文章访问相关 ==========
const accessCode = ref('')
const verifying = ref(false)
const verifyError = ref('')

const requestNote = ref('')
const requesting = ref(false)
const requestSent = ref(false)
const generatedCode = ref('')

// 验证访问码
const verifyCode = async () => {
  if (!accessCode.value || verifying.value) return
  
  verifying.value = true
  verifyError.value = ''
  
  try {
    const res = await $fetch<{ valid: boolean; error?: string }>('/access-tokens/verify', {
      baseURL: config.public.apiBase,
      method: 'POST',
      body: {
        code: accessCode.value,
        articleId: data.value?.article?.ArticleId
      }
    })
    
    if (res.valid) {
      // 验证成功，保存访问码到 localStorage（使用 slug 作为 key）
      localStorage.setItem(`access_code_slug_${slug}`, accessCode.value)
      
      // 重新请求文章（带上访问码）
      await refreshWithCode()
    } else {
      verifyError.value = res.error || '访问码无效'
    }
  } catch (err: any) {
    verifyError.value = err?.data?.error || '验证失败，请稍后重试'
  } finally {
    verifying.value = false
  }
}

// 使用访问码重新请求文章
const refreshWithCode = async () => {
  const savedCode = localStorage.getItem(`access_code_slug_${slug}`)
  
  if (savedCode) {
    // 带上访问码重新请求
    const newData = await $fetch<ApiResponse>(`${config.public.apiBase}/articles/${slug}`, {
      query: { accessCode: savedCode }
    })
    
    if (newData && !newData.requiresAccess) {
      // 更新数据
      data.value = newData
    }
  }
}

// 申请访问码
const requestAccess = async () => {
  if (requesting.value) return
  
  requesting.value = true
  
  try {
    const res = await $fetch<{ accessCode: string; message: string }>('/access-tokens/request', {
      baseURL: config.public.apiBase,
      method: 'POST',
      body: {
        articleId: data.value?.article?.ArticleId,
        note: requestNote.value || undefined
      }
    })
    
    generatedCode.value = res.accessCode
    requestSent.value = true
    
    // 保存申请的访问码到 localStorage（使用 slug 作为 key）
    localStorage.setItem(`access_code_slug_${slug}`, res.accessCode)
    // 同时更新输入框
    accessCode.value = res.accessCode
  } catch (err: any) {
    alert(err?.data?.error || '申请失败，请稍后重试')
  } finally {
    requesting.value = false
  }
}

// 页面加载时，如果需要授权且有保存的访问码，自动填入
onMounted(() => {
  if (data.value?.requiresAccess) {
    const savedCode = localStorage.getItem(`access_code_slug_${slug}`)
    if (savedCode) {
      accessCode.value = savedCode
      // 注意：首次请求已经带上 accessCode 了，如果还显示 requiresAccess
      // 说明 token 无效或未审批，不需要再自动验证，让用户手动点击或等待审批
    }
  }
})

// ========== TOC 侧边栏相关 ==========
const { headings, isMenuOpen: isTocOpen, closeMenu: closeTocSidebar } = useTableOfContents()

// 转换 TOC 数据为侧边栏格式
const tocItems = computed<SidebarItem[]>(() => {
  return headings.value.map(h => ({
    id: h.id,
    text: h.text,
    level: h.level,
    isMapSection: h.isMapSection
  }))
})

// 当前激活的 TOC 项（可以根据滚动位置更新）
const activeTocId = ref('')
let tocScrollTicking = false

// 监听滚动更新 Active 状态（核心逻辑）
const updateActiveTocCore = () => {
  tocScrollTicking = false
  if (import.meta.server || tocItems.value.length === 0) return
  
  // 查找所有标题元素
  const headingElements = tocItems.value.map(item => ({
    id: item.id,
    el: document.getElementById(item.id)
  })).filter(h => h.el)
  
  if (headingElements.length === 0) return
  
  // 找到距离视口顶部最近的标题
  const headerOffset = 100 // 偏移量
  
  // 倒序查找，找到第一个位置在视口上方的标题，或者视口内最靠上的标题
  for (let i = headingElements.length - 1; i >= 0; i--) {
    const { id, el } = headingElements[i]!;
    // 元素顶部距离相对于视口的偏移 + 滚动距离 = 元素绝对位置
    // 直接用 getBoundingClientRect 更方便
    const rect = el!.getBoundingClientRect()
    
    // 如果标题在视口上方一点点或者视口内
    if (rect.top <= headerOffset + 50) {
      activeTocId.value = id
      return
    }
  }
  
  // 如果都还没滚动到，默认选中第一个
  if (headingElements.length > 0 && !activeTocId.value) {
    activeTocId.value = headingElements[0]!.id
  }
}

// RAF 节流的滚动监听
const updateActiveToc = () => {
  if (!tocScrollTicking) {
    requestAnimationFrame(updateActiveTocCore)
    tocScrollTicking = true
  }
}

// 监听滚动
onMounted(() => {
  if (import.meta.client) {
    window.addEventListener('scroll', updateActiveToc, { passive: true })
    // 初始化一次
    setTimeout(updateActiveToc, 500)
  }
  
  if (data.value?.requiresAccess) {
    const savedCode = localStorage.getItem(`access_code_slug_${slug}`)
    if (savedCode) {
      accessCode.value = savedCode
    }
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    window.removeEventListener('scroll', updateActiveToc)
  }
})

// 监听 tocItems 变化，重新计算
watch(() => tocItems.value, () => {
    if (import.meta.client) {
      setTimeout(updateActiveToc, 500)
    }
})

// 处理 TOC 项点击
const handleTocSelect = (item: SidebarItem) => {
  activeTocId.value = item.id // 立即高亮点击项
  const el = document.getElementById(item.id)
  if (el) {
    const headerOffset = 80
    const elementPosition = el.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }
  closeTocSidebar()
}

// SEO
useHead({
  title: computed(() => data.value?.article?.Title || '文章详情'),
  meta: [
    { name: 'description', content: computed(() => data.value?.article?.Summary || '') }
  ]
})

const getImageUrl = (key: string) => {
  if (!key) return ''
  if (key.startsWith('http')) return key
  const imageBase = 'https://image.lixiang.page' 
  return `${imageBase}/${key}`
}

const formatDate = (dateStr: string) => {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric'
  })
}
</script>

<style scoped>
.blog-detail-container {
  /* 用于 sticky 定位的上下文 */
  position: relative;
}

.article-page {
  min-height: 80vh;
}

/* 顶部标题区，不包含在 article-layout 中 */
.standard-header {
  margin-bottom: 2rem;
}

/* 文章布局：左侧 TOC + 右侧内容 */
.article-layout {
  position: relative;
  display: flex;
}

.toc-sidebar-wrapper {
  position: absolute;
  right: 100%;
  top: 0;
  padding-right: 3rem;
  height: 100%;
}


.article-content-wrapper {
  flex: 1;
  min-width: 0;
}

/* 小屏时隐藏左侧 TOC wrapper */
@media (max-width: 1536px) {
  .toc-sidebar-wrapper {
    position: static;
    display: contents;
  }
}

.loading-state, .error-state, .private-access-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  text-align: center;
}



.back-link {
  margin-top: 20px;
  color: var(--muted-color);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s;
  padding: 8px 0;
  display: inline-block;
}

.back-link:hover {
  color: var(--text-color);
  text-decoration: underline;
}

/* 私密文章样式 */
.private-card {
  max-width: 400px;
  width: 100%;
  padding: 40px 30px;
}

.lock-icon {
  color: var(--muted-color);
  margin-bottom: 20px;
}

.private-access-state h1 {
  font-size: 1.5rem;
  margin-bottom: 10px;
  color: var(--text-color);
}

.hint-text {
  color: var(--muted-color);
  margin-bottom: 30px;
}

.access-form {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.access-form input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid var(--border-color);
  background: rgba(125, 125, 125, 0.08);
  color: var(--text-color);
  font-size: 1rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  border-radius: 4px;
  transition: border-color 0.2s, background-color 0.2s;
}

.access-form input:focus {
  outline: none;
  border-color: var(--text-color);
  background: rgba(125, 125, 125, 0.12);
}

.access-form button {
  padding: 12px 24px;
  background: var(--button-bg);
  color: var(--button-text);
  border: none;
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  border: 1px solid var(--border-color);
}

.access-form button:hover:not(:disabled) {
  background: var(--button-text);
  color: var(--button-bg);
  transform: translateY(-1px);
}

.access-form button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--muted-color);
}

.error-text {
  color: #e53935;
  font-size: 0.85rem;
  margin-bottom: 20px;
}

.divider {
  display: flex;
  align-items: center;
  margin: 30px 0;
  color: var(--muted-color);
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: currentColor;
  opacity: 0.3;
}

.divider span {
  padding: 0 15px;
  font-size: 0.85rem;
}

.request-section {
  text-align: left;
}

.request-hint {
  color: var(--muted-color);
  font-size: 0.9rem;
  margin-bottom: 15px;
  text-align: center;
}

.request-section textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  background: rgba(125, 125, 125, 0.08);
  color: var(--text-color);
  font-size: 0.9rem;
  resize: none;
  margin-bottom: 15px;
  box-sizing: border-box;
  border-radius: 4px;
  transition: border-color 0.2s, background-color 0.2s;
}

.request-section textarea:focus {
  outline: none;
  border-color: var(--text-color);
  background: rgba(125, 125, 125, 0.12);
}

.request-btn {
  width: 100%;
  padding: 12px;
  background: transparent;
  color: var(--text-color);
  border: 1px solid var(--border-color);
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
}

.request-btn:hover:not(:disabled) {
  background: var(--text-color) !important;
  color: var(--bg-color) !important;
  border-color: var(--text-color) !important;
}

.request-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  color: var(--muted-color) !important;
  border-color: var(--muted-color);
}

.request-result {
  padding: 24px;
  background: rgba(125, 125, 125, 0.08); /* 使用半透明背景自适应黑白模式 */
  border: 1px solid var(--border-color);
  border-radius: 8px;
  text-align: center;
}

.success-text {
  color: #4caf50;
  font-weight: 500;
  margin-bottom: 15px;
  font-size: 1rem;
}

.code-display {
  font-size: 1.1rem;
  margin-bottom: 15px;
  color: var(--text-color);
}

.code-display strong {
  font-family: monospace;
  letter-spacing: 3px;
  color: var(--text-color);
  font-size: 1.4rem;
  display: block;
  margin-top: 8px;
  font-weight: bold;
}

.info-text {
  color: var(--muted-color);
  font-size: 0.85rem;
  line-height: 1.5;
}

.standard-header {
  /* max-width: 800px; text content width */
  margin: 0 auto 40px;
  /* padding: 0 20px; */
}

.cover-wrapper {
  width: 100%;
  /* height: 300px; or a fixed height */
  margin-bottom: 30px;
  overflow: hidden;
  /* border-radius: 0; by default it is square so fine */
  /* 图片长宽比例 */
  aspect-ratio: 16 / 9;
}

.article-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.title-block {
  text-align: left;
}

.title-block h1 {
  font-size: 2.2rem;
  margin-bottom: 15px;
  line-height: 1.3;
  color: var(--text-color);
}

.meta {
  color: var(--muted-color);
  font-size: 0.9rem;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
}

.meta .reading-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.meta .reading-time svg {
  opacity: 0.7;
}

/* Ensure MarkdownView content is centered if not handled by component */
:deep(.markdown-body) {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px 60px;
}

</style>
